import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import emailVerificationService from '../services/emailVerification';

const VerifyEmail: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Token de verificación no encontrado');
        setLoading(false);
        return;
      }

      try {
        const response = await emailVerificationService.verifyEmail(token);
        setSuccess(true);
        setMessage(response.message);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" gutterBottom>
            Verificando tu email...
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Por favor espera mientras verificamos tu dirección de correo electrónico.
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Card elevation={3}>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <CheckCircleIcon 
              sx={{ 
                fontSize: 80, 
                color: 'success.main', 
                mb: 2 
              }} 
            />
            <Typography variant="h4" gutterBottom color="success.main">
              ¡Email Verificado!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {message || '¡Tu cuenta ha sido verificada exitosamente!'}
            </Typography>
            <Alert severity="success" sx={{ mb: 3 }}>
              Ya puedes acceder a todas las funcionalidades de Facto.
            </Alert>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGoToDashboard}
                size="large"
              >
                Ir al Dashboard
              </Button>
              <Button
                variant="outlined"
                onClick={handleGoToLogin}
                size="large"
              >
                Iniciar Sesión
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Card elevation={3}>
        <CardContent sx={{ textAlign: 'center', p: 4 }}>
          <ErrorIcon 
            sx={{ 
              fontSize: 80, 
              color: 'error.main', 
              mb: 2 
            }} 
          />
          <Typography variant="h4" gutterBottom color="error.main">
            Error de Verificación
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Typography variant="body1" sx={{ mb: 3 }}>
            El enlace de verificación puede haber expirado o ser inválido.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGoToLogin}
              size="large"
            >
              Ir a Iniciar Sesión
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/register')}
              size="large"
            >
              Registrarse Nuevamente
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default VerifyEmail;
